import { translate, useLocale } from "../../localization";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import moment from "moment";
import { useAuth } from "../../context/AuthContext";
import { useExperiences } from "../../context/ExperiencesContext";
import ExperiencesAPI from "../../services/ExperiencesAPI";
import GuideOptionsDropdown from "../GuideOptionsDropdown";
import SystemOptionsDropdown from "../SystemOptionsDropdown";
import { AppButton, AppTextField } from "../ui";

const buildInitialExperience = (experiences, organizationId, userId) => ({
  name: `Class ${(experiences?.length || 0) + 1}, ${moment().format("MMM Do YYYY")}`,
  organizationId: organizationId || "",
  userId: userId || "",
  systemId: "",
  guideId: "",
});

const CreateExperienceDialog = ({
  open,
  onClose,
  onCreated,
  title = translate("Create Experience"),
  submitLabel = translate("Create"),
}) => {
  const locale = useLocale();
  const { experiences, fetchData } = useExperiences();
  const { organizationId, userId } = useAuth();
  const initialExperience = useMemo(
    () => buildInitialExperience(experiences, organizationId, userId),
    [experiences, organizationId, userId, locale]
  );
  const [newExperience, setNewExperience] = useState(initialExperience);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setNewExperience(initialExperience);
    setErrorMessage("");
  }, [initialExperience, open]);

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    onClose?.();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewExperience((previousExperience) => ({
      ...previousExperience,
      [name]: value,
    }));
  };

  const handleCreateExperience = async () => {
    const trimmedName = newExperience.name.trim();
    if (!trimmedName || !newExperience.systemId || !newExperience.guideId) {
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    const createdExperience = {
      ...newExperience,
      name: trimmedName,
      experienceId: crypto.randomUUID(),
    };

    try {
      await ExperiencesAPI.post("experiences", createdExperience, {
        requiresAuth: true,
      });
      await fetchData();
      onCreated?.(createdExperience);
      onClose?.();
    } catch (error) {
      console.error("Error creating experience:", error);
      setErrorMessage(
        error?.response?.data?.message || translate("Unable to create an experience right now.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isSubmitDisabled =
    isSaving ||
    !newExperience.name.trim() ||
    !newExperience.systemId ||
    !newExperience.guideId;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "experiencesDialogPaper" }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <SystemOptionsDropdown
            value={newExperience.systemId}
            onChange={(systemId) =>
              setNewExperience((previousExperience) => ({
                ...previousExperience,
                systemId,
              }))
            }
          />
          <GuideOptionsDropdown
            value={newExperience.guideId}
            onChange={(guideId) =>
              setNewExperience((previousExperience) => ({
                ...previousExperience,
                guideId,
              }))
            }
          />
          <AppTextField
            fullWidth
            margin="normal"
            label={translate("Experience Name")}
            name="name"
            value={newExperience.name}
            onChange={handleInputChange}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <AppButton
          onClick={handleCreateExperience}
          disabled={isSubmitDisabled}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? translate("Creating...") : submitLabel}
        </AppButton>
        <AppButton onClick={handleClose} kind="secondary" disabled={isSaving}>{translate("Cancel")}</AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateExperienceDialog;
